import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // For FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only clear auth on 401 Unauthorized, not on other errors
    if (error.response?.status === 401) {
      // Check if it's actually an authentication error vs a database error
      const errorMessage = error.response?.data?.error || '';
      if (errorMessage.includes('token') || errorMessage.includes('unauthorized') || errorMessage.includes('authentication')) {
        // Only clear auth for actual auth errors, not database errors
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Don't force navigation here - let React handle it
        console.log('Authentication error detected, clearing auth state');
      }
    }
    return Promise.reject(error);
  }
);

// Public API (no authentication required)
export const publicAPI = {
  // Get school information
  getSchoolInfo: () => api.get('/public/school-info'),
  
  // Get announcements/news
  getAnnouncements: (params) => api.get('/public/announcements', { params }),
  getAnnouncement: (id) => api.get(`/public/announcements/${id}`),
  
  // Get academic programs
  getPrograms: () => api.get('/public/programs'),
  getProgram: (id) => api.get(`/public/programs/${id}`),
  
  // Get teachers/staff (removed - not needed for school)
  // getFaculty: () => api.get('/public/faculty'),
  // getFacultyMember: (id) => api.get(`/public/faculty/${id}`),
  
  // Get facilities
  getFacilities: () => api.get('/public/facilities'),
  getFacility: (id) => api.get(`/public/facilities/${id}`),
  
  // Get events
  getEvents: (params) => api.get('/public/events', { params }),
  getEvent: (id) => api.get(`/public/events/${id}`),
  
  // Get gallery
  getGallery: (params) => api.get('/public/gallery', { params }),
  
  // Contact form submission
  submitContactForm: (data) => api.post('/public/contact', data),
  
  // Newsletter subscription
  subscribeNewsletter: (email) => api.post('/public/newsletter/subscribe', { email }),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password, confirmPassword) => api.post(`/auth/reset-password/${token}`, { password, confirmPassword }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// Student API (authenticated)
export const studentAPI = {
  getProfile: () => api.get('/student/profile'),
  updateProfile: (data) => api.put('/student/profile', data),
  
  // Academic records
  getAcademicRecords: () => api.get('/student/academic-records'),
  getGrades: (params) => api.get('/student/grades', { params }),
  getAttendance: (params) => api.get('/student/attendance', { params }),
  
  // Assignments and exams
  getAssignments: (params) => api.get('/student/assignments', { params }),
  getAssignment: (id) => api.get(`/student/assignments/${id}`),
  submitAssignment: (id, data) => api.post(`/student/assignments/${id}/submit`, data),
  
  getExams: (params) => api.get('/exams', { params }),
  getExam: (id) => api.get(`/exams/${id}`),
  
  // Timetable
  getTimetable: (params) => api.get('/student/timetable', { params }),
  
  // Notifications
  getNotifications: (params) => api.get('/student/notifications', { params }),
  markNotificationRead: (id) => api.put(`/student/notifications/${id}/read`),
};

// Parent API (authenticated)
export const parentAPI = {
  getProfile: () => api.get('/parent/profile'),
  updateProfile: (data) => api.put('/parent/profile', data),
  
  // Children management
  getChildren: () => api.get('/parent/children'),
  getChild: (id) => api.get(`/parent/children/${id}`),
  
  // Academic records for children
  getChildAcademicRecords: (childId) => api.get(`/parent/children/${childId}/academic-records`),
  getChildGrades: (childId, params) => api.get(`/parent/children/${childId}/grades`, { params }),
  getChildAttendance: (childId, params) => api.get(`/parent/children/${childId}/attendance`, { params }),
  
  // Notifications
  getNotifications: (params) => api.get('/parent/notifications', { params }),
  markNotificationRead: (id) => api.put(`/parent/notifications/${id}/read`),
};

// Admission API
export const admissionAPI = {
  submitApplication: (data) => api.post('/admission/apply', data),
  getApplicationStatus: (applicationId) => api.get(`/admission/status/${applicationId}`),
  getApplicationForm: () => api.get('/admission/form'),
  uploadDocuments: (formData) => api.post('/admission/upload-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// User API (authenticated)
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/change-password', data),
  uploadProfileImage: (id, formData) => api.post(`/users/${id}/profile-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadAvatar: (formData) => api.post('/users/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// File Upload API
export const fileAPI = {
  uploadFile: (formData, type = 'general') => api.post(`/files/upload/${type}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getFile: (fileId) => api.get(`/files/${fileId}`),
};

export default api;
